<template>
    <div class="text-editor-container" :class="containerClasses">
        <!-- Toolbar Section (hidden only when fully disabled without readonly) -->
        <div v-if="!disabled || readonly" class="editor-toolbar-wrapper" :class="{ 'is-readonly': readonly }">
            <div class="editor-toolbar" aria-label="Text editor toolbar">
                <!-- History Group -->
                <b-btn-group size="sm" class="toolbar-group">
                    <b-btn v-if="!readonly" v-b-tooltip.hover :title="'Undo|Annuler' | tr" variant="light"
                        @click="exec('undo')" class="toolbar-btn" :disabled="readonly">
                        <i class="fas fa-undo"></i>
                    </b-btn>
                    <b-btn v-if="!readonly" v-b-tooltip.hover :title="'Redo|Refaire' | tr" variant="light"
                        @click="exec('redo')" class="toolbar-btn" :disabled="readonly">
                        <i class="fas fa-redo"></i>
                    </b-btn>
                </b-btn-group>

                <!-- Formatting Group -->
                <b-btn-group size="sm" class="toolbar-group">
                    <b-btn v-b-tooltip.hover :title="'Bold|Gras' | tr" variant="light"
                        :class="{ active: activeStates.bold }" @click="exec('bold')" class="toolbar-btn"
                        :disabled="readonly">
                        <i class="fas fa-bold"></i>
                    </b-btn>
                    <b-btn v-b-tooltip.hover :title="'Italic|Italique' | tr" variant="light"
                        :class="{ active: activeStates.italic }" @click="exec('italic')" class="toolbar-btn"
                        :disabled="readonly">
                        <i class="fas fa-italic"></i>
                    </b-btn>
                    <b-btn v-b-tooltip.hover :title="'Underline|Souligner' | tr" variant="light"
                        :class="{ active: activeStates.underline }" @click="exec('underline')" class="toolbar-btn"
                        :disabled="readonly">
                        <i class="fas fa-underline"></i>
                    </b-btn>
                    <b-btn v-b-tooltip.hover :title="'Strikethrough|Barrer' | tr" variant="light"
                        :class="{ active: activeStates.strikeThrough }" @click="exec('strikeThrough')"
                        class="toolbar-btn" :disabled="readonly">
                        <i class="fas fa-strikethrough"></i>
                    </b-btn>
                </b-btn-group>

                <!-- Alignment Group -->
                <b-btn-group size="sm" class="toolbar-group">
                    <b-btn v-b-tooltip.hover :title="'Align Left|Aligner à gauche' | tr" variant="light"
                        :class="{ active: activeStates.justifyLeft }" @click="exec('justifyLeft')" class="toolbar-btn"
                        :disabled="readonly">
                        <i class="fas fa-align-left"></i>
                    </b-btn>
                    <b-btn v-b-tooltip.hover :title="'Align Center|Aligner au centre' | tr" variant="light"
                        :class="{ active: activeStates.justifyCenter }" @click="exec('justifyCenter')"
                        class="toolbar-btn" :disabled="readonly">
                        <i class="fas fa-align-center"></i>
                    </b-btn>
                    <b-btn v-b-tooltip.hover :title="'Align Right|Aligner à droite' | tr" variant="light"
                        :class="{ active: activeStates.justifyRight }" @click="exec('justifyRight')" class="toolbar-btn"
                        :disabled="readonly">
                        <i class="fas fa-align-right"></i>
                    </b-btn>
                    <b-btn v-b-tooltip.hover :title="'Justify|Justifier' | tr" variant="light"
                        :class="{ active: activeStates.justifyFull }" @click="exec('justifyFull')" class="toolbar-btn"
                        :disabled="readonly">
                        <i class="fas fa-align-justify"></i>
                    </b-btn>
                </b-btn-group>

                <!-- Lists Group -->
                <b-btn-group size="sm" class="toolbar-group">
                    <b-btn v-b-tooltip.hover :title="'Bullet List|Liste à puces' | tr" variant="light"
                        :class="{ active: activeStates.insertUnorderedList }" @click="exec('insertUnorderedList')"
                        class="toolbar-btn" :disabled="readonly">
                        <i class="fas fa-list-ul"></i>
                    </b-btn>
                    <b-btn v-b-tooltip.hover :title="'Numbered List|Liste numérotée' | tr" variant="light"
                        :class="{ active: activeStates.insertOrderedList }" @click="exec('insertOrderedList')"
                        class="toolbar-btn" :disabled="readonly">
                        <i class="fas fa-list-ol"></i>
                    </b-btn>
                </b-btn-group>

                <!-- Clear Formatting -->
                <b-btn-group v-if="!readonly" size="sm" class="toolbar-group">
                    <b-btn v-b-tooltip.hover :title="'Clear Formatting|Effacer le format' | tr" variant="light"
                        @click="exec('removeFormat')" class="toolbar-btn clear-btn" :disabled="readonly">
                        <i class="fas fa-eraser"></i>
                    </b-btn>
                </b-btn-group>
            </div>
        </div>

        <!-- Editable Content Area -->
        <div class="editor-body">
            <div ref="editable" class="editor-content" :class="{ 'strip-colors': stripColors }"
                :contenteditable="!disabled && !readonly" @input="onInput" @keydown="onKeydown"
                @mouseup="updateToolbarStates" @keyup="updateToolbarStates" @focus="onFocus" @blur="onBlur"
                @paste="onPaste"></div>
            <div v-if="showPlaceholder" class="editor-placeholder" @click="focusEditor">
                {{ placeholder }}
            </div>
            <!-- Actions slot at bottom left -->
            <div v-if="$slots.actions" class="editor-actions">
                <slot name="actions"></slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: `text-editor`,
    props: {
        value: {
            type: String,
            default: ``,
        },
        placeholder: {
            type: String,
            default: ``,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        stripColors: {
            type: Boolean,
            default: false,
        },
        actionsOnHover: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isFocused: false,
            internalValue: ``,
            activeStates: {
                bold: false,
                italic: false,
                underline: false,
                strikeThrough: false,
                justifyLeft: false,
                justifyCenter: false,
                justifyRight: false,
                justifyFull: false,
                insertUnorderedList: false,
                insertOrderedList: false,
            },
        };
    },
    computed: {
        containerClasses() {
            return {
                "is-focused": this.isFocused,
                "is-disabled": this.disabled,
                "is-readonly": this.readonly,
                "actions-on-hover": this.actionsOnHover,
            };
        },
        showPlaceholder() {
            const text = this.$refs.editable ? this.$refs.editable.innerText.trim() : ``;
            return !this.isFocused && text === ``;
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                if (newVal !== this.internalValue) {
                    this.internalValue = newVal;
                    this.$nextTick(() => {
                        if (this.$refs.editable && this.$refs.editable.innerHTML !== newVal) {
                            this.$refs.editable.innerHTML = newVal || ``;
                        }
                    });
                }
            },
        },
    },
    mounted() {
        if (this.$refs.editable) {
            this.$refs.editable.innerHTML = this.value || ``;
        }
    },
    methods: {
        exec(command, value = null) {
            if (this.disabled || this.readonly) {
                return;
            }
            this.$refs.editable.focus();
            document.execCommand(command, false, value);
            this.updateToolbarStates();
            this.emitChange();
        },
        onInput() {
            if (this.disabled || this.readonly) {
                return;
            }
            this.emitChange();
            this.updateToolbarStates();
        },
        onKeydown(e) {
            if (this.disabled || this.readonly) {
                return;
            }
            if (e.key === `Tab`) {
                e.preventDefault();
                document.execCommand(`insertHTML`, false, `&nbsp;&nbsp;&nbsp;&nbsp;`);
            }
        },
        onPaste(e) {
            if (this.disabled || this.readonly) {
                return;
            }
            e.preventDefault();
            const text = e.clipboardData.getData(`text/plain`);
            document.execCommand(`insertText`, false, text);
        },
        onFocus() {
            this.isFocused = true;
            this.$emit(`focus`);
        },
        onBlur() {
            this.isFocused = false;
            this.$emit(`blur`);
        },
        emitChange() {
            const content = this.$refs.editable.innerHTML;
            if (content !== this.internalValue) {
                this.internalValue = content;
                this.$emit(`input`, content);
                this.$emit(`change`, content);
            }
        },
        updateToolbarStates() {
            const commands = Object.keys(this.activeStates);
            for (let i = 0; i < commands.length; i++) {
                const command = commands[i];
                try {
                    this.activeStates[command] = document.queryCommandState(command);
                } catch (e) {
                    this.activeStates[command] = false;
                }
            }
        },
        focusEditor() {
            this.$refs.editable.focus();
        },
    },
};
</script>

<style lang="scss" scoped>
$editor-border-color: #e2e8f0;
$editor-focus-ring: rgba(66, 153, 225, 0.15);
$editor-accent: #3182ce;
$toolbar-bg: #f8fafc;
$btn-hover-bg: #edf2f7;

.text-editor-container {
    display: flex;
    flex-direction: column;
    border-radius: 6px;
    background: #ffffff;
    overflow: hidden;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover:not(.is-disabled) {
        border-color: darken($editor-border-color, 5%);
    }

    &.is-focused {
        border-color: $editor-accent;
        box-shadow: 0 0 0 3px $editor-focus-ring;
    }

    &.is-disabled,
    &.is-readonly {
        background: #f9f9f9;
        cursor: default;

        .editor-content {
            cursor: default;
            user-select: text;
        }
    }

    &.actions-on-hover {
        .editor-actions {
            opacity: 0;
            transition: opacity 0.2s ease;
        }

        &:hover .editor-actions {
            opacity: 1;
        }
    }
}

.editor-toolbar-wrapper {
    background: $toolbar-bg;
    border-bottom: 1px solid $editor-border-color;
    padding: 6px 8px;

    &.is-readonly {
        opacity: 0.6;
        pointer-events: none;
    }
}

.editor-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.toolbar-group {
    border-radius: 4px;
    overflow: hidden;

    &+.toolbar-group {
        margin-left: 4px;
    }
}

.toolbar-btn {
    background: #ffffff;
    border: 1px solid $editor-border-color;
    color: #4a5568;
    min-width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.15s ease-in-out;

    i {
        font-size: 0.75rem;
    }

    &:hover {
        background: $btn-hover-bg;
        color: $editor-accent;
        border-color: darken($editor-border-color, 10%);
    }

    &.active {
        background: $editor-accent;
        color: #ffffff;
        border-color: $editor-accent;
    }

    &:focus {
        box-shadow: none;
    }

    &.clear-btn:hover {
        background: #fff5f5;
        border-color: #feb2b2;
        color: #e53e3e;
    }
}

.btn-group .btn:not(:last-child) {
    border-right: none;
}

.editor-body {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
}

.editor-content {
    flex: 1;
    padding: 12px;
    min-height: 50px;
    outline: none;
    line-height: 1.5;
    color: #2d3748;
    font-size: 0.9rem;
    overflow-y: auto;

    text-align: left;

    p {
        margin-bottom: 0.5rem;
    }

    ul,
    ol {
        padding-left: 1.5rem;
        margin-bottom: 0.5rem;
    }
}

:deep(.strip-colors [style]) {
    color: #2d3748 !important;
    background-color: transparent !important;
}

.editor-actions {
    display: flex;
    align-items: center;
    padding: 2px 4px;
}

.editor-placeholder {
    position: absolute;
    top: 12px;
    left: 12px;
    color: #a0aec0;
    pointer-events: none;
    font-size: 0.9rem;
    font-style: italic;
}

.editor-content::-webkit-scrollbar {
    width: 6px;
}

.editor-content::-webkit-scrollbar-track {
    background: transparent;
}

.editor-content::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;

    &:hover {
        background: #a0aec0;
    }
}
</style>
